/**
 * 💌 MATCHES TAB - Architecture Mozart/Jobs/Tesla
 * 
 * L'onglet des matchs et conversations.
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Heart, Clock, Sparkles, Gift, Gamepad2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { useChat } from '../../../context/heartbeats';

export const MatchesTab = ({ 
  customerId, 
  isDarkMode, 
  matches = [],
  onOpenChat,
  onSendGift,
  onStartGame
}) => {
  const [filter, setFilter] = useState('all'); // all, unread, recent

  const filteredMatches = matches.filter(match => {
    if (filter === 'unread') return match.unreadCount > 0;
    if (filter === 'recent') {
      const lastMsg = new Date(match.lastMessageAt);
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return lastMsg > dayAgo;
    }
    return true;
  });

  if (matches.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">💔</div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Pas encore de matchs
        </h3>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
          Continue à swiper pour trouver ta moitié ! 💜
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'all', label: 'Tous', emoji: '💜' },
          { id: 'unread', label: 'Non lus', emoji: '💬' },
          { id: 'recent', label: 'Récents', emoji: '🔥' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              filter === f.id
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                : isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.emoji} {f.label}
          </button>
        ))}
      </div>

      {/* Liste des matchs */}
      <div className="space-y-3">
        {filteredMatches.map((match, index) => (
          <motion.div
            key={match.matchId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`p-4 rounded-2xl cursor-pointer transition-all hover:scale-[1.01] ${
              isDarkMode 
                ? 'bg-gray-800/70 hover:bg-gray-750 border border-gray-700' 
                : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
            }`}
            onClick={() => onOpenChat?.(match)}
          >
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl overflow-hidden">
                  {match.photo ? (
                    <img src={match.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    match.avatar || '😊'
                  )}
                </div>
                {/* Online indicator */}
                {match.isOnline && (
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className={`font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {match.name}
                  </h4>
                  {match.isNew && (
                    <span className="px-2 py-0.5 rounded-full bg-pink-500 text-white text-xs">
                      Nouveau !
                    </span>
                  )}
                </div>
                <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {match.lastMessage || "Dis bonjour ! 👋"}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatTime(match.lastMessageAt)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col items-end gap-2">
                {match.unreadCount > 0 && (
                  <span className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs flex items-center justify-center font-bold">
                    {match.unreadCount}
                  </span>
                )}
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSendGift?.(match);
                    }}
                    className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <Gift className="w-4 h-4 text-yellow-500" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartGame?.(match);
                    }}
                    className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <Gamepad2 className="w-4 h-4 text-purple-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* Compatibilité */}
            {match.compatibility > 0 && (
              <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                    {match.compatibility}% compatible
                  </span>
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                      style={{ width: `${match.compatibility}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Utilitaire de formatage
function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return "À l'instant";
  if (diff < 3600000) return `${Math.floor(diff/60000)} min`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default MatchesTab;
